import { useEffect, useMemo, useRef, useState } from "react";
import { Drawer } from "vaul";
import { TgScreen } from "../components/TgScreen";
import { Card, MainButton, Spinner } from "../components/atoms";
import { Ico } from "../lib/icons";
import { truncateAddr } from "../lib/format";
import { haptic, hapticSelection } from "../lib/haptics";
import { QrScanner, extractAddressFromScan } from "../lib/qr-scanner";
import { useShieldedKeys } from "../shielded/keys-context";
import { useShieldedBalance } from "../shielded/balance-context";
import { useUnlockedWallet } from "../wallet";
import { useDevice } from "../lib/device-context";
import { reportProofTiming, reportProofOOM } from "../lib/telemetry";
import { recordTransaction } from "../lib/tx-history";
import { explainError } from "../lib/error-hints";
import {
  atomicToHuman,
  atomicToNumber,
  decimalsForOutputAsset,
} from "../lib/atomic-units";
import { Send as SendApi, getSpendables } from "../shielded";
import {
  getZecMemoUA,
  type SwapQuote,
} from "../lib/maya-client";
import {
  getLeokitMayaQuote,
  getLeokitMayaDeposit,
  getLeokitBestQuote,
  getLeokitDeposit,
} from "../lib/leokit-swap";
import { EVM_CHAINS, type EvmChainId } from "../evm/chains";
import { signAndSendEvmTx } from "../evm/send";
import { useEvmHoldings } from "../evm/use-evm-holdings";
import type { TokenBalance } from "../evm/balances";

// Same conservative ZIP-317 ceiling we use on the Send form. A swap
// adds a memo-note output so the fee is slightly higher than a plain
// shielded send — 30000 zats covers the common case.
const FEE_BUFFER_ZATS = 30_000;

// Refresh the live MAYA quote on this cadence while a quote is shown.
const QUOTE_REFRESH_MS = 30_000;

// Debounce on form-input changes before we fire an auto-quote. Roughly
// matches the typing pause that signals "user is done editing this
// field" without burning bandwidth on every keystroke.
const AUTO_QUOTE_DEBOUNCE_MS = 400;

// Gas reserve we subtract from MAX for native EVM assets — leaves ~0.001
// of the native unit so the user can still cover gas on the swap tx.
// ERC-20s ignore this because gas is paid separately from the principal.
const EVM_NATIVE_GAS_RESERVE = 0.001;

// LeoKit /quote doesn't return `in_asset_decimal` in the offers payload,
// so we need a local table for parsing the user's amount input and the
// pre-quote balance gate. Resolution order: explicit asset → ticker
// prefix → fallback 18 (covers most ERC-20s besides USDC/USDT).
const KNOWN_DECIMALS: Record<string, number> = {
  ZEC: 8,
  BTC: 8,
  ETH: 18,
  USDC: 6,
  USDT: 6,
  POL: 18,
  MATIC: 18,
};

// LeoKit asset id → ticker, e.g. `"ETH.USDC-0xA0b8…"` → `"USDC"`.
const tickerFromAssetId = (assetId: string): string => {
  const after = assetId.split(".")[1] ?? assetId;
  return (after.split("-")[0] ?? after).toUpperCase();
};

// LeoKit asset id → ticker before the chain dot, e.g. `"ETH.USDC-…"` → `"ETH"`.
const chainPrefixFromAssetId = (assetId: string): string => {
  return (assetId.split(".")[0] ?? "").toUpperCase();
};

// Decimal count for a LeoKit asset id, used to convert user-entered
// amounts → atomic units before we have a quote. ZEC=8, ETH=18, USDC=6,
// fallback 18 for unknown ERC-20s.
const inAssetDecimal = (assetId: string): number => {
  const ticker = tickerFromAssetId(assetId);
  return KNOWN_DECIMALS[ticker] ?? 18;
};

// Is this asset id a native gas token on its chain? (BTC.BTC, ZEC.ZEC,
// ETH.ETH, ARB.ETH, BASE.ETH, OP.ETH, POLYGON.POL). We use this to
// decide whether MAX should subtract a gas reserve.
const isNativeAsset = (assetId: string): boolean => {
  const [chain, rest] = assetId.split(".");
  if (!chain || !rest) return false;
  if (rest.includes("-")) return false; // contract suffix → ERC-20
  return true;
};

// LeoKit chain prefix → EvmChainId used by viem-config map in evm/send.ts.
const evmChainIdForAsset = (assetId: string): EvmChainId | null => {
  const prefix = chainPrefixFromAssetId(assetId);
  switch (prefix) {
    case "ETH":
      return "ETH";
    case "ARB":
      return "ARB";
    case "BASE":
      return "BASE";
    case "OP":
      return "OP";
    case "POLYGON":
      return "POLYGON";
    default:
      return null;
  }
};

// Block-explorer URL for a successful EVM tx — used by the success card
// link. Hardcoded per task spec instead of round-tripping through viem.
const EVM_EXPLORER_TX: Record<EvmChainId, string> = {
  ETH: "https://etherscan.io/tx/",
  ARB: "https://arbiscan.io/tx/",
  BASE: "https://basescan.org/tx/",
  OP: "https://optimistic.etherscan.io/tx/",
  POLYGON: "https://polygonscan.com/tx/",
};

// Human-friendly chain label for the success card / from-row.
const EVM_CHAIN_LABEL: Record<EvmChainId, string> = {
  ETH: "Ethereum",
  ARB: "Arbitrum",
  BASE: "Base",
  OP: "Optimism",
  POLYGON: "Polygon",
};

const parseAmount = (raw: string): number => {
  const cleaned = raw.replace(/\s+/g, "").replace(",", ".");
  return Number.parseFloat(cleaned);
};

const zatsToAmountString = (zats: number): string => {
  if (zats <= 0) return "0";
  return (zats / 1e8).toFixed(8).replace(/\.?0+$/, "");
};

// Generic atomic-units → display string for any source-asset decimal.
// Trims trailing zeros so the input field stays compact.
const atomicToAmountString = (atomic: number, decimals: number): string => {
  if (atomic <= 0) return "0";
  const v = atomic / Math.pow(10, decimals);
  // Cap the decimal places so a huge ERC-20 balance doesn't fill the
  // input with a 19-digit fraction. 8 places fits ZEC + most ERC-20s.
  return v.toFixed(Math.min(8, decimals)).replace(/\.?0+$/, "");
};

// Open an external URL using Telegram's in-app browser when available
// (keeps the user inside the chat shell), falling back to window.open
// for browser preview / non-Telegram contexts. openLink isn't in our
// TelegramWebApp typedef yet so we reach for it via a narrow cast.
const openExternal = (url: string) => {
  const wa = window.Telegram?.WebApp as
    | (TelegramWebApp & { openLink?: (u: string) => void })
    | undefined;
  if (wa?.openLink) {
    wa.openLink(url);
  } else {
    window.open(url, "_blank");
  }
};

type Stage =
  | { kind: "form" }
  | { kind: "quoting" }
  | {
      kind: "quoted";
      quote: SwapQuote;
      /**
       * LeoKit protocol id picked for this quote ("mayachain",
       * "thorchain", "chainflip", …). For ZEC origin this is always
       * "mayachain"; for EVM origin we surface whatever LeoKit flagged
       * OPTIMAL, and pass it back to /deposit verbatim.
       */
      protocol: string;
      depositAddress: string;
      memoUa: string;
      memoText: string;
      feeZats: number;
      fetchedAt: number;
      refreshing: boolean;
    }
  | { kind: "sending"; progress: SendApi.SendProgress }
  // ZEC origin success — txid is a Zcash txid, fee is in zats.
  | {
      kind: "success";
      mode: "zec";
      txid: string;
      fee: number;
      quote: SwapQuote;
    }
  // EVM origin success — hash is the broadcast EVM tx hash, chainId
  // tells the success card which explorer to link to.
  | {
      kind: "success";
      mode: "evm";
      hash: string;
      chainId: EvmChainId;
      from: `0x${string}`;
      quote: SwapQuote;
      fromAsset: string;
      amountIn: string;
    }
  | { kind: "error"; message: string };

interface Props {
  onBack: () => void;
}

// Curated picker — most-used destinations for ZEC → other-chain swaps.
// Each entry is `MAYA asset id` + display data.
interface Target {
  id: string;
  symbol: string; // "USDC", "ETH", "BTC"
  chain: string; // "Ethereum", "Arbitrum", "Bitcoin"
  color: string; // hex, used for the token-glyph circle
}

const TARGETS: Target[] = [
  {
    id: "ZEC.ZEC",
    symbol: "ZEC",
    chain: "Zcash",
    color: "#F4B728",
  },
  {
    id: "BTC.BTC",
    symbol: "BTC",
    chain: "Bitcoin",
    color: "#F7931A",
  },
  {
    id: "ETH.ETH",
    symbol: "ETH",
    chain: "Ethereum",
    color: "#627EEA",
  },
  {
    id: "ETH.USDC-0XA0B86991C6218B36C1D19D4A2E9EB0CE3606EB48",
    symbol: "USDC",
    chain: "Ethereum",
    color: "#2775CA",
  },
  {
    id: "ARB.USDC-0XAF88D065E77C8CC2239327C5EDB3A432268E5831",
    symbol: "USDC",
    chain: "Arbitrum",
    color: "#2775CA",
  },
  {
    id: "ARB.ETH",
    symbol: "ETH",
    chain: "Arbitrum",
    color: "#28A0F0",
  },
];

// Find a TARGETS entry by id; falls back to the first when missing.
const targetById = (id: string): Target => TARGETS.find((t) => t.id === id) ?? TARGETS[0]!;

// Destination-asset decimal count for a quote. We prefer LeoKit's
// `out_asset_decimal` (18 for ETH, 6 for USDC, 8 for BTC), then fall
// back to the asset id's ticker if the field is missing — the OLD
// fallback of `8` made ETH display 10^10 too large when the field was
// absent, which is exactly the "wei in the UI" failure mode we want
// to make impossible.
const dstDecimalsFromQuote = (q: SwapQuote, assetId: string): number =>
  decimalsForOutputAsset(q.raw as { out_asset_decimal?: number }, assetId);

// Convenience: format a raw atomic value (string or number) from a
// quote payload as a human-readable decimal string for display.
// Centralised here so all the swap UI sites format identically.
const fmtFromQuote = (
  raw: string | number | bigint,
  q: SwapQuote,
  assetId: string,
  displayDecimals = 6,
): string => atomicToHuman(raw, dstDecimalsFromQuote(q, assetId), displayDecimals);

// Numeric variant — only use this when you need to multiply (e.g.
// applying a slippage %). Precision drops above 2^53 atomic units;
// for display use `fmtFromQuote`.
const numFromQuote = (
  raw: string | number | bigint,
  q: SwapQuote,
  assetId: string,
): number => atomicToNumber(raw, dstDecimalsFromQuote(q, assetId));

const SLIPPAGE_PRESETS = [0.5, 1, 2] as const;
type SlippagePct = number; // raw % e.g. 1 means 1%

export function SwapScreen({ onBack }: Props) {
  const wallet = useUnlockedWallet();
  const { keys } = useShieldedKeys();
  const { balance, refresh: refreshBalance } = useShieldedBalance();
  const { assessment } = useDevice();
  // EVM holdings unlock the "pay from any asset" feature. While
  // `holdings` is null we restrict the picker to ZEC and gate quotes
  // (the LeoKit /quote endpoint needs the user's EVM address as
  // `origin` for EVM sources).
  const { holdings: evmHoldings } = useEvmHoldings();

  const [fromAsset, setFromAsset] = useState<string>("ZEC.ZEC");
  // Default destination: BTC.BTC — first non-ZEC entry in TARGETS so a
  // fresh form doesn't show ZEC on both sides.
  const [toAsset, setToAsset] = useState<string>(
    TARGETS.find((t) => t.id !== "ZEC.ZEC")?.id ?? TARGETS[0]!.id,
  );
  const [toAddress, setToAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [stage, setStage] = useState<Stage>({ kind: "form" });
  const [stageStartedAt, setStageStartedAt] = useState<number>(0);
  const [elapsedS, setElapsedS] = useState(0);

  // Slippage tolerance is UI-only for now (MAYA's quote API on our
  // current SwapQuoteInput surface doesn't accept a slippage parameter).
  // We surface it so the user can dial it in and we wire it through to
  // the min-received display + future memo `limit`. See buildSwapMemo
  // for where this would flow.
  const [slippagePct, setSlippagePct] = useState<SlippagePct>(1);
  const [slippageCustom, setSlippageCustom] = useState<string>("");

  // Slippage % parsed back out of the custom field when active. Falls
  // back to the most-recent preset if the custom box is empty or junk.
  // Hoisted above the auto-quote effect so the dep array can reference
  // it without a use-before-declare error.
  const effectiveSlippagePct = useMemo<SlippagePct>(() => {
    if (slippageCustom.trim() === "") return slippagePct;
    const n = Number.parseFloat(slippageCustom.replace(",", "."));
    if (!Number.isFinite(n) || n <= 0) return slippagePct;
    return Math.min(50, n);
  }, [slippagePct, slippageCustom]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [fromPickerOpen, setFromPickerOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [fromPickerSearch, setFromPickerSearch] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Forces a re-render of the "Updated Ns ago" string without owning the
  // tick logic in 5 different places. Bumped from a once-per-second
  // interval while a quote is on screen.
  const [, setFreshnessTick] = useState(0);

  // Bumped by the "Try again" button on the error stage so the
  // auto-quote effect re-fires even when the form inputs haven't
  // changed. Including stage.kind in the dep array instead would
  // cause spurious refires on every stage transition.
  const [retryNonce, setRetryNonce] = useState(0);

  // AbortController so a stale in-flight quote doesn't clobber a fresh
  // one when the user edits an input mid-fetch.
  const quoteAbort = useRef<AbortController | null>(null);
  // Track whether the last quote landed, so the rate output can animate
  // a subtle skeleton-replace when a silent refresh delivers a new value.
  const [pulseOnce, setPulseOnce] = useState(false);

  useEffect(() => {
    if (stage.kind !== "sending") {
      setElapsedS(0);
      return;
    }
    const tick = () =>
      setElapsedS(Math.floor((Date.now() - stageStartedAt) / 1000));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [stage.kind, stageStartedAt]);

  // Lock the WebView close gesture while the prover is running — a
  // mid-Groth16 swipe-close throws away ~30 s of CPU work plus the user's
  // deposit memo. Telegram surfaces a confirmation dialog while this is
  // enabled. We always unlock on exit so the form / success / error
  // screens behave normally.
  useEffect(() => {
    if (stage.kind !== "sending") return;
    const wa = window.Telegram?.WebApp;
    wa?.enableClosingConfirmation?.();
    return () => {
      wa?.disableClosingConfirmation?.();
    };
  }, [stage.kind]);

  // Reset → debounce → auto-fetch on input change.
  //
  // 1) Any edit invalidates the prior quote and the prior in-flight
  //    fetch (a Geometric Brownian price feed makes 5s-old quotes
  //    misleading, and routing decisions are amount-sensitive).
  // 2) After the user stops typing for AUTO_QUOTE_DEBOUNCE_MS we
  //    trigger a fresh quote automatically. No "Get quote" tap needed.
  //
  // We pre-check the same gates `refreshQuote` does so we don't spin up
  // the debounce timer for unsubmittable forms. The actual gates inside
  // refreshQuote still apply (defence in depth).
  useEffect(() => {
    if (stage.kind === "quoted" || stage.kind === "quoting") {
      quoteAbort.current?.abort();
      setStage({ kind: "form" });
    }
    // Don't fire while sending/success/error — only auto-quote from
    // the editable form stage.
    if (
      stage.kind === "sending" ||
      stage.kind === "success" ||
      stage.kind === "error"
    ) {
      return;
    }
    if (!amountValid) return;
    if (toAddress.trim().length < 8) return;
    if (sameAsset) return;
    if (fromIsZec ? !keys : !evmHoldings?.address) return;
    if (overBalance) return;
    const t = setTimeout(() => {
      void refreshQuote(false);
    }, AUTO_QUOTE_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // signer-ready flags (`keys`, `evmHoldings?.address`) are part of
    // the deps so a quote fires the moment the wallet finishes
    // unlocking / EVM holdings resolve — even if the user filled the
    // form earlier. `retryNonce` is bumped by the error-stage "Try
    // again" button to re-fire the effect when inputs haven't changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fromAsset,
    toAsset,
    toAddress,
    amount,
    effectiveSlippagePct,
    keys,
    evmHoldings?.address,
    retryNonce,
  ]);

  // Heartbeat for "Updated Ns ago" — only ticks while a quote is shown.
  useEffect(() => {
    if (stage.kind !== "quoted") return;
    const t = setInterval(() => setFreshnessTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [stage.kind]);

  // Silent auto-refresh every QUOTE_REFRESH_MS while a quote is on
  // screen. Edits cancel the timer via the reset effect above.
  useEffect(() => {
    if (stage.kind !== "quoted") return;
    const t = setInterval(() => {
      void refreshQuote(true);
    }, QUOTE_REFRESH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage.kind]);

  // Branch flag — true when the source asset is the shielded ZEC
  // pseudo-asset. Drives MAYA-only quote path, shielded-send broadcast,
  // and the ZEC-specific success card.
  const fromIsZec = fromAsset === "ZEC.ZEC";

  // Source-asset decimal table — ZEC=8, ETH=18, USDC=6, etc. LeoKit
  // /quote doesn't return `in_asset_decimal` so we lean on a local map
  // for parsing user input + the pre-quote balance gate.
  const fromDecimals = inAssetDecimal(fromAsset);
  const fromTicker = fromIsZec ? "ZEC" : tickerFromAssetId(fromAsset);

  // Parsed numeric amount, in human units. `amountAtomic` is the same
  // value scaled to atomic units (zats for ZEC, wei for ETH, …) — that's
  // what LeoKit /quote wants in the `amount` query param.
  const amountIn = parseAmount(amount);
  const amountValid = Number.isFinite(amountIn) && amountIn > 0;
  const amountAtomic = amountValid
    ? Math.round(amountIn * Math.pow(10, fromDecimals))
    : 0;
  // Kept as an alias for ZEC-specific code paths (shielded send,
  // success-card text, on-chain fee math) so the existing logic still
  // reads naturally.
  const amountZats = fromIsZec ? amountAtomic : 0;

  // Resolve the matching EVM token row (when source is an EVM asset).
  // Search across every chain — `token_symbol` is the unique LeoKit id.
  const evmFromToken: TokenBalance | null = useMemo(() => {
    if (fromIsZec || !evmHoldings) return null;
    for (const chain of evmHoldings.perChain) {
      // LeoKit/Rango return mixed-case asset ids (the contract address
      // suffix is checksummed); TARGETS uses upper-case. Compare both
      // sides upper-cased so picks from either source resolve.
      const t = chain.tokens.find(
        (tok) => tok.token_symbol.toUpperCase() === fromAsset.toUpperCase(),
      );
      if (t) return t;
    }
    return null;
  }, [fromAsset, fromIsZec, evmHoldings]);

  // Spendable in atomic units. ZEC reads from the shielded balance
  // context; EVM reads from the holdings hook (already-decimalised, so
  // we re-scale to atomic for the gate comparison).
  const spendableAtomic = fromIsZec
    ? balance.netBalanceZats
    : evmFromToken
      ? Math.floor(evmFromToken.balance * Math.pow(10, fromDecimals))
      : 0;
  // ZEC has a fee buffer; EVM gas comes out of native, not from the
  // principal of a token swap, so we only enforce buffer on ZEC.
  const overBalance =
    amountValid &&
    (fromIsZec
      ? amountAtomic + FEE_BUFFER_ZATS > spendableAtomic
      : amountAtomic > spendableAtomic);
  // Legacy alias used by the existing ZEC-specific render code below.
  const spendable = balance.netBalanceZats;

  const target = targetById(toAsset);
  // The from→to "swap to the same asset" degenerate case. LeoKit will
  // happily quote it but the deposit is nonsense. Disable the CTA.
  const sameAsset = fromAsset.toUpperCase() === toAsset.toUpperCase();

  // Precompute the actual ZIP-317 fee like Send does. We attempt the
  // same selectCoins call so the displayed fee matches what the signer
  // will use. If selection throws (insufficient notes), we fall back to
  // the conservative buffer for display.
  const computeLocalFee = async (): Promise<number> => {
    if (!keys) return FEE_BUFFER_ZATS;
    try {
      const { notes } = await getSpendables(keys.ufvk);
      const picked = SendApi.selectCoins({
        notes,
        amount_zats: amountZats,
        num_outputs: 1,
        num_transparent_outputs: 1, // t-vault principal output
      });
      return picked.fee_zats;
    } catch {
      return FEE_BUFFER_ZATS;
    }
  };

  // Single source of truth for fetching a quote — used by initial quote
  // and silent refresh. silent=true keeps the previous payload visible
  // and only swaps when the new one lands (with a subtle pulse). The
  // function branches on origin: ZEC routes via MAYAChain only (shielded
  // memo flow), EVM routes via the best-offer aggregator.
  const refreshQuote = async (silent = false) => {
    if (!amountValid) return;
    if (toAddress.trim().length < 8) return;
    if (sameAsset) return;
    // EVM origin needs a derived address — bail until the holdings hook
    // surfaces one. (Silent path is silent: don't error-out on race.)
    if (!fromIsZec && !evmHoldings?.address) return;
    const ac = new AbortController();
    quoteAbort.current?.abort();
    quoteAbort.current = ac;
    if (!silent) setStage({ kind: "quoting" });
    else if (stage.kind === "quoted") {
      setStage({ ...stage, refreshing: true });
    }
    try {
      // We resolve the inbound vault + memo by hitting LeoKit's /deposit
      // endpoint, but that's bound to a specific quote_id and consumes a
      // server-side allocation slot — we'd burn one on every silent
      // 30s refresh. So we only quote here; the deposit handshake
      // happens lazily at confirm time. The memo UA is fetched eagerly
      // for ZEC since the shielded send composes its memo-note from it.
      const origin = fromIsZec
        ? (wallet?.addresses.zecTransparent ??
          wallet?.addresses.zecUnified ??
          keys?.unifiedAddress ??
          "")
        : (evmHoldings?.address ?? "");
      const quoteInput = {
        from_asset: fromAsset,
        to_asset: toAsset,
        // LeoKit expects WHOLE units (1 ZEC = "1", not "100000000" zats).
        // We normalise the user's text input (handles comma decimal sep,
        // trailing zeros) so the wire value is stable across edits.
        amount: String(amountIn),
        origin,
        destination: toAddress.trim(),
        slippage_bps: Math.round(effectiveSlippagePct * 100),
      };
      // ZEC path: getLeokitMayaQuote filters to MAYAChain (only
      // protocol with the shielded-memo deposit flow). We also fetch
      // the memo UA + local ZIP-317 fee in parallel since the shielded
      // send needs both. For EVM origins memoUa+feeZats are inert.
      const [memoUa, quoteAny, feeZats] = await Promise.all([
        fromIsZec ? getZecMemoUA() : Promise.resolve(""),
        fromIsZec
          ? getLeokitMayaQuote(quoteInput, ac.signal)
          : getLeokitBestQuote(quoteInput, ac.signal),
        fromIsZec ? computeLocalFee() : Promise.resolve(0),
      ]);
      if (ac.signal.aborted) return;
      const quote = quoteAny;
      const protocol = quote.protocol;
      // Convert the LeoKit quote into the legacy SwapQuote shape the
      // rest of this screen renders against. We pull a single approximate
      // "protocol fee in target-asset" out of the per-leg fee array
      // (LeoKit's fees use target-asset amounts but split across
      // affiliate/outbound/network) by summing only those legs whose
      // `asset` matches the destination — the others are reported in
      // intermediate assets and aren't useful to flatten.
      const targetAssetMatches = quote.fees.filter(
        (f) => f.asset?.toUpperCase() === toAsset.toUpperCase(),
      );
      const protoFeeTargetUnits = targetAssetMatches.reduce(
        (s, f) => s + (f.amount ?? 0),
        0,
      );
      const protoFeeAtomic = (
        protoFeeTargetUnits *
        Math.pow(10, quote.out_asset_decimal)
      ).toFixed(0);
      const legacyShape: SwapQuote = {
        expected_amount_out: String(quote.expected_amount_out),
        total_swap_seconds: quote.total_swap_seconds,
        fees: protoFeeTargetUnits > 0 ? { total: protoFeeAtomic } : {},
        memo: null,
        notes: null,
        raw: {
          ...quote.raw,
          // Echo metadata the UI's quote freshness countdown can read.
          quote_id: quote.quote_id,
          out_asset_decimal: quote.out_asset_decimal,
          total_fees_usd: quote.total_fees_usd,
          expected_amount_out_usd: quote.expected_amount_out_usd,
          input_amount_usd: quote.input_amount_usd,
          ttl_seconds: quote.ttl_seconds,
          expires_at_ms: quote.expires_at_ms,
        },
      };
      // depositAddress + memoText are TBD until confirm: we hold the
      // quote_id in `raw` and resolve them inside onConfirm.
      setStage({
        kind: "quoted",
        quote: legacyShape,
        protocol,
        depositAddress: "",
        memoUa,
        memoText: "",
        feeZats,
        fetchedAt: Date.now(),
        refreshing: false,
      });
      if (silent) {
        setPulseOnce(true);
        setTimeout(() => setPulseOnce(false), 420);
      }
    } catch (e) {
      if (ac.signal.aborted) return;
      if (silent && stage.kind === "quoted") {
        // Silent refresh failure: keep showing the previous quote and
        // just drop the refreshing badge. Real errors only show up on
        // first quote.
        setStage({ ...stage, refreshing: false });
        return;
      }
      setStage({
        kind: "error",
        message: e instanceof Error ? e.message : String(e),
      });
      haptic("error");
    }
  };

  const onConfirm = async () => {
    if (stage.kind !== "quoted") return;
    if (!wallet) return;
    const { quote, memoUa, protocol } = stage;

    setConfirmOpen(false);
    haptic("medium");
    // ZEC needs the keys; EVM doesn't (signs from the mnemonic via
    // viem inside signAndSendEvmTx).
    if (fromIsZec && !keys) return;
    // Pick an initial progress stage that matches what's actually
    // happening: ZEC needs witness sync + proof gen first, EVM goes
    // straight to "broadcasting" — there are no zero-knowledge proofs
    // to compute, so showing "Syncing witnesses…" for an EVM swap
    // misleads the user about why they're waiting.
    setStage({
      kind: "sending",
      progress: { stage: fromIsZec ? "syncing-witnesses" : "broadcasting" },
    });
    setStageStartedAt(Date.now());
    const t0 = performance.now();
    try {
      // Resolve the deposit address + memo NOW (not at quote time) — the
      // LeoKit aggregator binds these to a specific quote_id and consumes
      // an allocation slot, so deferring keeps silent refreshes free.
      const quoteId = String(
        (quote.raw as { quote_id?: unknown }).quote_id ?? "",
      );
      if (!quoteId) {
        throw new Error(
          "Internal: quote_id missing from quote payload. Re-fetch the quote and try again.",
        );
      }
      if (fromIsZec) {
        // ── ZEC origin: shielded send to a t-address with memo-note ──
        if (!keys) throw new Error("Shielded keys not unlocked");
        const deposit = await getLeokitMayaDeposit(quoteId);
        const depositAddress = deposit.deposit_address;
        const memoText = deposit.memo;
        const res = await SendApi.sendShielded(
          {
            ufvk: keys.ufvk,
            seed_phrase: wallet.phrase,
            account: keys.account,
            recipient: depositAddress, // t-address principal
            amount_zats: amountZats,
            memo_note: { recipient_ua: memoUa, memo_text: memoText },
          },
          (p) => setStage({ kind: "sending", progress: p }),
        );
        const total_ms = Math.round(performance.now() - t0);
        reportProofTiming({
          kind: "swap",
          device_class: assessment?.class ?? "ok",
          total_ms,
          proving_ms: null,
          decompress_ms: null,
          spend_count: 0,
          output_count: 2, // principal t-out + memo-note sapling
        });
        recordTransaction({
          kind: "swap",
          txid: res.txid,
          amount_zats: amountZats,
          fee_zats: res.fee_zats,
          recipient: depositAddress,
          recipient_username: null,
          swap_to_asset: toAsset,
          swap_to_address: toAddress.trim(),
          memo: memoText,
          origin_chain: "zec",
          from_asset: fromAsset,
        });
        void refreshBalance();
        setStage({
          kind: "success",
          mode: "zec",
          txid: res.txid,
          fee: res.fee_zats,
          quote,
        });
        haptic("success");
      } else {
        // ── EVM origin: LeoKit returns a viem-ready unsigned tx; we
        // sign it with the derived account on the chain we picked. No
        // approval step — if the user picks an ERC-20 lacking allowance,
        // the broadcast will revert and we surface that as an error.
        const chainId = evmChainIdForAsset(fromAsset);
        if (!chainId) {
          throw new Error(
            `Unsupported EVM source chain for asset ${fromAsset}`,
          );
        }
        const deposit = await getLeokitDeposit(quoteId, protocol);
        if (deposit.type !== "EVM" || !deposit.evm_tx) {
          throw new Error(
            `LeoKit /deposit returned ${deposit.type} for an EVM swap. Try a different route.`,
          );
        }
        const { hash, from } = await signAndSendEvmTx({
          phrase: wallet.phrase,
          chainId,
          unsigned: deposit.evm_tx,
        });
        recordTransaction({
          kind: "swap",
          txid: hash,
          // EVM swaps don't denominate the spend in zats — the Wallet's
          // detail sheet renders amount_zats=0 as a hint and uses the
          // origin_chain + from_amount fields below to display the real
          // source amount in its native ticker.
          amount_zats: 0,
          fee_zats: 0,
          recipient: deposit.evm_tx.to,
          recipient_username: null,
          swap_to_asset: toAsset,
          swap_to_address: toAddress.trim(),
          memo: deposit.memo ?? "",
          origin_chain: chainId,
          from_asset: fromAsset,
          from_amount: amount,
        });
        setStage({
          kind: "success",
          mode: "evm",
          hash,
          chainId,
          from,
          quote,
          fromAsset,
          amountIn: amount,
        });
        haptic("success");
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      const looksLikeOom = /out of memory|RangeError|wasm.*memory/i.test(message);
      if (looksLikeOom) {
        reportProofOOM({
          kind: "swap",
          device_class: assessment?.class ?? "ok",
          message,
        });
      }
      // ERC-20 allowance hint — viem surfaces "transfer amount exceeds
      // allowance" / 0xfb8f41b2 selector. Add a one-line note so the
      // user knows to approve manually (we don't ship Permit2 yet).
      const looksLikeAllowance =
        !fromIsZec &&
        /allowance|approval|insufficient.*approved|ERC20.*transfer|0xfb8f41b2/i.test(
          message,
        );
      setStage({
        kind: "error",
        message: looksLikeAllowance
          ? `${message}\n\nThis swap needs an ERC-20 approval first. Approve the router for ${fromTicker} on the chain explorer, then try again.`
          : message,
      });
      haptic("error");
    }
  };

  // ──────────────────────────────────────────────────────────────────
  // Render: success / error short-circuits live here, the main form
  // body follows below.
  // ──────────────────────────────────────────────────────────────────

  if (stage.kind === "success") {
    // Two distinct success cards: ZEC origin → shielded send completed,
    // link Zcash + MAYAScan. EVM origin → tx broadcast on the source
    // chain, link the appropriate block explorer.
    if (stage.mode === "zec") {
      return (
        <TgScreen title="Swap broadcast" onClose={onBack}>
          <div style={{ padding: "16px 16px 24px" }}>
            <Card style={{ padding: 18 }}>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>
                <span className="bounce-in">✓</span> Swap on the way
              </div>
              <Section label="Sending">
                {amount} ZEC →{" "}
                {fmtFromQuote(stage.quote.expected_amount_out, stage.quote, target.id)}{" "}
                {target.symbol}
              </Section>
              <Section label="ZEC tx id">
                <span style={{ fontFamily: "monospace", fontSize: 11, wordBreak: "break-all" }}>
                  {stage.txid}
                </span>
              </Section>
              <Section label="Est. arrival">
                ~{Math.max(60, stage.quote.total_swap_seconds)} s
              </Section>
              <div style={{ marginTop: 12, fontSize: 12, color: "var(--hint)" }}>
                The Zcash deposit is on-chain. MAYA observes after 1-2
                confirmations, then signs the outbound on the destination
                chain — track both legs below.
              </div>
            </Card>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button
                onClick={() =>
                  openExternal(`https://blockchair.com/zcash/transaction/${stage.txid}`)
                }
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "transparent",
                  color: "var(--link, #2c8acb)",
                  border: "1px solid var(--link, #2c8acb)",
                  borderRadius: 12,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "var(--font-text)",
                }}
              >
                Zcash tx ↗
              </button>
              <button
                onClick={() =>
                  openExternal(`https://www.mayascan.org/tx/${stage.txid}`)
                }
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "transparent",
                  color: "var(--link, #2c8acb)",
                  border: "1px solid var(--link, #2c8acb)",
                  borderRadius: 12,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "var(--font-text)",
                }}
              >
                MAYA tx ↗
              </button>
            </div>
            <button
              onClick={onBack}
              style={{
                marginTop: 10,
                width: "100%",
                padding: "12px",
                background: "var(--tg-theme-button-color, #2c8acb)",
                color: "var(--tg-theme-button-text-color, white)",
                border: "none",
                borderRadius: 12,
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Done
            </button>
          </div>
        </TgScreen>
      );
    }
    // EVM mode
    const explorerUrl = `${EVM_EXPLORER_TX[stage.chainId]}${stage.hash}`;
    const explorerLabel = `${EVM_CHAIN_LABEL[stage.chainId]} tx ↗`;
    return (
      <TgScreen title="Swap broadcast" onClose={onBack}>
        <div style={{ padding: "16px 16px 24px" }}>
          <Card style={{ padding: 18 }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>
              <span className="bounce-in">✓</span> Swap on the way
            </div>
            <Section label="Sending">
              {stage.amountIn} {tickerFromAssetId(stage.fromAsset)} →{" "}
              {fmtFromQuote(stage.quote.expected_amount_out, stage.quote, target.id)}{" "}
              {target.symbol}
            </Section>
            <Section label={`${EVM_CHAIN_LABEL[stage.chainId]} tx hash`}>
              <span style={{ fontFamily: "monospace", fontSize: 11, wordBreak: "break-all" }}>
                {stage.hash}
              </span>
            </Section>
            <Section label="Est. arrival">
              ~{Math.max(60, stage.quote.total_swap_seconds)} s
            </Section>
            <div style={{ marginTop: 12, fontSize: 12, color: "var(--hint)" }}>
              Transaction broadcast from {truncateAddr(stage.from, 6, 4)}. Once
              it confirms, the aggregator picks it up and signs the outbound
              leg on the destination chain.
            </div>
          </Card>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button
              onClick={() => openExternal(explorerUrl)}
              style={{
                flex: 1,
                padding: "12px",
                background: "transparent",
                color: "var(--link, #2c8acb)",
                border: "1px solid var(--link, #2c8acb)",
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "var(--font-text)",
              }}
            >
              {explorerLabel}
            </button>
          </div>
          <button
            onClick={onBack}
            style={{
              marginTop: 10,
              width: "100%",
              padding: "12px",
              background: "var(--tg-theme-button-color, #2c8acb)",
              color: "var(--tg-theme-button-text-color, white)",
              border: "none",
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>
      </TgScreen>
    );
  }

  if (stage.kind === "error") {
    const hint = explainError(stage.message);
    return (
      <TgScreen title="Swap failed" onClose={onBack}>
        <div style={{ padding: "16px 16px 24px" }}>
          <Card style={{ padding: 18 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: "var(--destructive, #d33)" }}>
              {hint.headline}
            </div>
            {hint.suggestion && (
              <div style={{ marginTop: 8, color: "var(--text-soft)", fontSize: 13, lineHeight: 1.45 }}>
                {hint.suggestion}
              </div>
            )}
            <details style={{ marginTop: 14 }}>
              <summary
                style={{
                  fontSize: 11,
                  color: "var(--hint)",
                  cursor: "pointer",
                  userSelect: "none",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Technical details
              </summary>
              <div
                style={{
                  marginTop: 6,
                  fontFamily: "monospace",
                  fontSize: 11,
                  color: "var(--hint)",
                  wordBreak: "break-word",
                }}
              >
                {stage.message}
              </div>
            </details>
          </Card>
          <button
            onClick={() => {
              // Reset to form AND bump the retry nonce so the
              // auto-quote effect re-fires even with the same inputs
              // — the user's intent in tapping "Try again" is "redo
              // exactly what just failed", not "go back and edit".
              setStage({ kind: "form" });
              setRetryNonce((n) => n + 1);
            }}
            style={{
              marginTop: 16,
              width: "100%",
              padding: "12px",
              background: "var(--tg-theme-button-color, #2c8acb)",
              color: "var(--tg-theme-button-text-color, white)",
              border: "none",
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      </TgScreen>
    );
  }

  // ──────────────────────────────────────────────────────────────────
  // Main form / quote / sending body
  // ──────────────────────────────────────────────────────────────────
  const isBusy = stage.kind === "quoting" || stage.kind === "sending";

  // Primary CTA copy + state, picked from the current form/quote state.
  const cta = computeCta({
    stageKind: stage.kind,
    amount,
    amountValid,
    overBalance,
    toAddressTrim: toAddress.trim(),
    // ZEC needs shielded keys; EVM only needs the holdings hook to have
    // resolved the derived address (so /quote has an `origin` to send).
    hasSigner: fromIsZec ? !!keys : !!evmHoldings?.address,
    sameAsset,
    fromTicker,
  });

  // Pretty "From" metadata for the AssetPill on the pay-side card. ZEC
  // gets the shielded-yellow theme; EVM assets borrow the chain colour
  // from EVM_CHAINS.
  const fromMeta = (() => {
    if (fromIsZec) {
      return {
        symbol: "ZEC",
        chain: "Zcash · shielded",
        color: "#F4B728",
      };
    }
    const prefix = chainPrefixFromAssetId(fromAsset);
    const chainCfg = EVM_CHAINS.find((c) => c.id === prefix);
    return {
      symbol: fromTicker,
      chain: chainCfg?.name ?? prefix,
      color: chainCfg?.color ?? "#888",
    };
  })();

  // USD value of the input + output sides for the Uniswap-style
  // subtitle under each amount. From-side USD comes from the live quote
  // (LeoKit returns `input_amount_usd`); to-side from
  // `expected_amount_out_usd`. While the quote is pending or absent we
  // fall back to a local estimate using the EVM token's `price` field
  // (LeoKit /balances ships it). ZEC has no local price feed so we
  // wait for the quote there.
  const liveQuote = stage.kind === "quoted" ? stage.quote : null;
  const liveQuoteRaw = liveQuote?.raw as
    | { input_amount_usd?: number; expected_amount_out_usd?: number }
    | undefined;
  const localInputUsd =
    !fromIsZec && evmFromToken && amountValid && evmFromToken.price > 0
      ? amountIn * evmFromToken.price
      : null;
  const inputUsd = liveQuoteRaw?.input_amount_usd ?? localInputUsd ?? null;
  const outputUsd = liveQuoteRaw?.expected_amount_out_usd ?? null;

  return (
    <TgScreen title="Swap" bottomNav="swap" sheetOpen={pickerOpen || confirmOpen || fromPickerOpen ? <></> : undefined}>
      <div style={{ padding: "12px 16px 24px" }}>
        {/* ─── "You pay" card ─────────────────────────────────────── */}
        <SwapPanel
          label="You pay"
          error={amountValid && overBalance}
          right={
            <FromBalancePill
              amount={spendableAtomic}
              decimals={fromDecimals}
              ticker={fromTicker}
              onMax={() => {
                // ZEC: reserve ZIP-317 fee buffer (zats). EVM native:
                // reserve ~0.001 of the native unit for gas. EVM ERC-20:
                // full balance (gas comes from native, separate token).
                if (fromIsZec) {
                  const max = Math.max(0, spendableAtomic - FEE_BUFFER_ZATS);
                  setAmount(zatsToAmountString(max));
                } else if (isNativeAsset(fromAsset)) {
                  const reserveAtomic = Math.round(
                    EVM_NATIVE_GAS_RESERVE * Math.pow(10, fromDecimals),
                  );
                  const max = Math.max(0, spendableAtomic - reserveAtomic);
                  setAmount(atomicToAmountString(max, fromDecimals));
                } else {
                  setAmount(atomicToAmountString(spendableAtomic, fromDecimals));
                }
                haptic("light");
              }}
              disabled={isBusy}
            />
          }
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <BigAmountInput
              value={amount}
              onChange={setAmount}
              placeholder="0"
              disabled={isBusy}
            />
            <AssetPill
              symbol={fromMeta.symbol}
              chain={fromMeta.chain}
              color={fromMeta.color}
              onClick={() => {
                hapticSelection();
                setFromPickerOpen(true);
              }}
              disabled={isBusy}
            />
          </div>
          <UsdSubtitle
            usd={inputUsd}
            loading={stage.kind === "quoting"}
            placeholder={amountValid ? "—" : "$0.00"}
          />
          {amountValid && overBalance && (
            <div
              style={{
                marginTop: 6,
                fontSize: 11,
                color: "var(--danger, #FF3B30)",
                fontWeight: 500,
              }}
            >
              {fromIsZec
                ? "Amount + fee exceeds your spendable balance."
                : `Insufficient ${fromTicker} balance.`}
            </div>
          )}
        </SwapPanel>

        {/* ─── Centered swap-direction handle ─────────────────────────
            Tap to flip from/to assets. We also auto-fill the destination
            address with the wallet's own corresponding address (UA when
            swapping into ZEC, derived EVM 0x… when swapping into an EVM
            asset) so the user doesn't have to paste their own address
            into their own wallet. */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <button
            type="button"
            className="swap-handle"
            onClick={() => {
              if (isBusy) return;
              haptic("light");
              const newFrom = toAsset;
              const newTo = fromAsset;
              setFromAsset(newFrom);
              setToAsset(newTo);
              // Auto-fill destination so the user doesn't have to type
              // their own address when bringing funds INTO the wallet.
              if (newTo === "ZEC.ZEC" && keys?.unifiedAddress) {
                setToAddress(keys.unifiedAddress);
              } else if (
                newTo.match(/^(ETH|ARB|BASE|OP|POLYGON)\./) &&
                evmHoldings?.address
              ) {
                setToAddress(evmHoldings.address);
              } else {
                setToAddress("");
              }
            }}
            disabled={isBusy}
            aria-label="Flip direction"
          >
            <Ico.swapArrows />
          </button>
        </div>

        {/* ─── "You receive" card ───────────────────────────────────── */}
        <SwapPanel label="You receive">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <ReceiveAmount
              quote={liveQuote}
              loading={stage.kind === "quoting"}
              pulse={pulseOnce}
              toAssetId={toAsset}
            />
            <AssetPill
              symbol={target.symbol}
              chain={target.chain}
              color={target.color}
              onClick={() => {
                hapticSelection();
                setPickerOpen(true);
              }}
              disabled={isBusy}
            />
          </div>
          <UsdSubtitle
            usd={outputUsd}
            loading={stage.kind === "quoting"}
            placeholder="$0.00"
          />
        </SwapPanel>

        {/* ─── Destination address input ───────────────────────────── */}
        <div className="address-row">
          <span style={{ color: "var(--hint)", display: "inline-flex" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </span>
          <input
            value={toAddress}
            onChange={(e) => setToAddress(e.target.value)}
            placeholder={
              target.symbol === "BTC"
                ? "bc1q… recipient address"
                : target.symbol === "ZEC"
                  ? "u1… or t1… recipient address"
                  : "0x… recipient address"
            }
            disabled={isBusy}
          />
          <button
            type="button"
            className="scan-btn"
            onClick={() => {
              haptic("light");
              setScanOpen(true);
            }}
            disabled={isBusy}
            aria-label="Scan QR"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <rect x="3" y="3" width="6" height="6" rx="1" />
              <rect x="15" y="3" width="6" height="6" rx="1" />
              <rect x="3" y="15" width="6" height="6" rx="1" />
              <path d="M15 15h2v2h-2zM19 15h2v2h-2zM15 19h2v2h-2zM19 19h2v2h-2z" />
            </svg>
            Scan
          </button>
        </div>

        {/* ─── Live quote section ──────────────────────────────────── */}
        {stage.kind === "quoted" && (
          <QuoteSummary
            quote={stage.quote}
            target={target}
            amountIn={amountIn}
            fromTicker={fromTicker}
            showZecNetworkFee={fromIsZec}
            feeZats={stage.feeZats}
            fetchedAt={stage.fetchedAt}
            refreshing={stage.refreshing}
            slippagePct={effectiveSlippagePct}
            protocol={stage.protocol}
            onOpenSettings={() => {
              hapticSelection();
              setSettingsOpen(true);
            }}
            onRefresh={() => {
              haptic("light");
              void refreshQuote(true);
            }}
          />
        )}

        {settingsOpen && (
          <SlippagePanel
            current={slippagePct}
            custom={slippageCustom}
            onPreset={(p) => {
              setSlippagePct(p);
              setSlippageCustom("");
              hapticSelection();
            }}
            onCustom={(v) => setSlippageCustom(v)}
            onClose={() => setSettingsOpen(false)}
          />
        )}

        {/* ─── Sending stage (preserved) ───────────────────────────── */}
        {stage.kind === "sending" && (
          <Card
            style={{
              padding: 14,
              marginTop: 12,
              background: "rgba(46, 187, 96, 0.06)",
              border: "1px solid rgba(46, 187, 96, 0.18)",
              borderRadius: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 8,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {progressLabel(stage.progress.stage)}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--hint)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {fmtElapsed(elapsedS)}
              </div>
            </div>
            <div style={{ marginTop: 4, fontSize: 12, color: "var(--hint)" }}>
              {stage.progress.params?.label ?? progressHint(stage.progress.stage)}
            </div>
            {stage.progress.params && <ProgressBar pct={stage.progress.params.pct} />}
            {elapsedS >= 45 && stage.progress.stage === "loading-params" && (
              <div
                style={{
                  marginTop: 10,
                  fontSize: 11,
                  color: "var(--warning, #FFB020)",
                  lineHeight: 1.5,
                }}
              >
                Decompression is slower than usual on this device. Hang on — or
                tap Cancel below and try again from the form.
              </div>
            )}
            <button
              type="button"
              onClick={() => setStage({ kind: "form" })}
              style={{
                marginTop: 12,
                width: "100%",
                background: "transparent",
                color: "var(--hint)",
                border: "1px solid var(--hairline, #2c333a)",
                borderRadius: 10,
                padding: "8px",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "var(--font-text)",
              }}
            >
              Cancel
            </button>
          </Card>
        )}

        {/* ─── Primary CTA ─────────────────────────────────────────── */}
        <div style={{ marginTop: 16 }}>
          <MainButton
            text={cta.text}
            disabled={cta.disabled || isBusy}
            destructive={cta.destructive}
            loading={stage.kind === "quoting" || stage.kind === "sending"}
            onClick={() => {
              if (cta.action === "confirm") {
                hapticSelection();
                setConfirmOpen(true);
              }
            }}
          />
        </div>
      </div>

      {/* ─── Token picker bottom sheet (destination) ────────────────── */}
      <TokenPickerSheet
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        search={pickerSearch}
        onSearch={setPickerSearch}
        selectedId={toAsset}
        excludeId={fromAsset}
        onSelect={(id) => {
          setToAsset(id);
          // Auto-fill destination when the user picks ZEC or an EVM
          // asset that matches the wallet's own address — they're
          // bringing funds into THIS wallet so typing their own
          // address is friction.
          if (id === "ZEC.ZEC" && keys?.unifiedAddress) {
            setToAddress(keys.unifiedAddress);
          } else if (
            id.match(/^(ETH|ARB|BASE|OP|POLYGON)\./) &&
            evmHoldings?.address
          ) {
            setToAddress(evmHoldings.address);
          } else {
            // Destination chain isn't one of ours (BTC, etc.) — clear
            // any stale ZEC/EVM auto-fill so the user is forced to
            // paste a real external address.
            setToAddress("");
          }
          setPickerOpen(false);
          setPickerSearch("");
          hapticSelection();
        }}
      />

      {/* ─── From-asset picker (ZEC + every held EVM asset) ─────────── */}
      <FromAssetPickerSheet
        open={fromPickerOpen}
        onClose={() => setFromPickerOpen(false)}
        search={fromPickerSearch}
        onSearch={setFromPickerSearch}
        zecBalanceZats={balance.netBalanceZats}
        evmHoldings={evmHoldings}
        selectedId={fromAsset}
        excludeId={toAsset}
        onSelect={(id) => {
          setFromAsset(id);
          // Wipe the input on source change — its numeric meaning
          // (ZEC vs ETH vs USDC) changes with the asset.
          setAmount("");
          setFromPickerOpen(false);
          setFromPickerSearch("");
          haptic("light");
        }}
      />

      {/* ─── Confirmation sheet ─────────────────────────────────────── */}
      <ConfirmSheet
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        stage={stage.kind === "quoted" ? stage : null}
        target={target}
        amountIn={amount}
        fromTicker={fromTicker}
        fromIsZec={fromIsZec}
        fromAddress={evmHoldings?.address ?? null}
        slippagePct={effectiveSlippagePct}
        toAddress={toAddress.trim()}
        onConfirm={onConfirm}
        disabled={
          (fromIsZec && !keys) ||
          (!fromIsZec && !evmHoldings?.address) ||
          isBusy ||
          stage.kind !== "quoted"
        }
      />
      <QrScanner
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onDecode={(text) => {
          setToAddress(extractAddressFromScan(text));
          setScanOpen(false);
        }}
      />
    </TgScreen>
  );
}

// ─── Card wrappers ────────────────────────────────────────────────────

function SwapPanel({
  label,
  right,
  children,
  error,
}: {
  label: string;
  right?: React.ReactNode;
  children: React.ReactNode;
  /** Adds a subtle danger-tint border when the user has typed an
   *  over-balance value — same visual as a form-field error. */
  error?: boolean;
}) {
  return (
    <div className={`swap-card${error ? " is-error" : ""}`}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: 12,
            color: "var(--hint)",
            fontWeight: 500,
            letterSpacing: "-0.005em",
          }}
        >
          {label}
        </span>
        {right}
      </div>
      {children}
    </div>
  );
}

function BigAmountInput({
  value,
  onChange,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  disabled?: boolean;
}) {
  return (
    <input
      className="amount-input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      inputMode="decimal"
    />
  );
}

// Generic balance pill: works for any source asset by taking the
// atomic amount + decimal count, not just ZEC zats.
function FromBalancePill({
  amount,
  decimals,
  ticker,
  onMax,
  disabled,
}: {
  /** Spendable balance in atomic units (zats / wei / 1e6 USDC). */
  amount: number;
  decimals: number;
  ticker: string;
  onMax: () => void;
  disabled?: boolean;
}) {
  const display = amount > 0 ? (amount / Math.pow(10, decimals)).toFixed(4) : "0";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span
        style={{
          fontSize: 11,
          color: "var(--hint)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {display} {ticker}
      </span>
      <button
        type="button"
        className="max-pill"
        onClick={onMax}
        disabled={disabled || amount <= 0}
      >
        MAX
      </button>
    </div>
  );
}

// USD-equivalent subtitle that sits below each amount input/output —
// the small "$1,234.56" caption you see in Uniswap. Renders a slim
// skeleton during quote fetches so the row height stays stable.
function UsdSubtitle({
  usd,
  loading,
  placeholder,
}: {
  usd: number | null;
  loading: boolean;
  placeholder: string;
}) {
  if (loading) {
    return (
      <span
        className="skel"
        aria-hidden
        style={{
          display: "inline-block",
          marginTop: 8,
          width: 70,
          height: 12,
          borderRadius: 4,
        }}
      />
    );
  }
  const text =
    usd != null && usd > 0
      ? `$${usd.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : placeholder;
  return (
    <div
      style={{
        marginTop: 6,
        fontSize: 12,
        color: "var(--hint)",
        fontVariantNumeric: "tabular-nums",
        letterSpacing: "-0.005em",
        minHeight: 14,
      }}
    >
      {text}
    </div>
  );
}

// Asset pill: glyph circle + symbol + chain caption + chevron. Used by
// both the "You pay" (fixed=ZEC) and "You receive" (tappable) cards.
function AssetPill({
  symbol,
  chain,
  color,
  onClick,
  fixed,
  disabled,
}: {
  symbol: string;
  chain: string;
  color: string;
  onClick?: () => void;
  fixed?: boolean;
  disabled?: boolean;
}) {
  const content = (
    <>
      <TokenGlyph symbol={symbol} color={color} />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          minWidth: 0,
          paddingRight: fixed ? 4 : 0,
        }}
      >
        <span
          style={{
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            lineHeight: 1.15,
          }}
        >
          {symbol}
        </span>
        <span
          style={{
            fontSize: 10,
            color: "var(--hint)",
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            maxWidth: 120,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {chain}
        </span>
      </div>
      {!fixed && (
        <span
          style={{
            color: "var(--hint)",
            display: "inline-flex",
            marginLeft: -2,
          }}
        >
          <Ico.chevDown />
        </span>
      )}
    </>
  );
  if (fixed) {
    return <div className="asset-pill fixed">{content}</div>;
  }
  return (
    <button
      type="button"
      className="asset-pill"
      onClick={onClick}
      disabled={disabled}
    >
      {content}
    </button>
  );
}

// Token glyph: a coloured circle with the symbol's first letter. We
// don't ship a static icon set for the 5-token TARGETS list — a
// monogram on the chain colour reads cleanly and stays consistent.
// Adds a subtle radial highlight for a tiny bit of dimensionality.
function TokenGlyph({
  symbol,
  color,
  size = 28,
}: {
  symbol: string;
  color: string;
  size?: number;
}) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        background: `radial-gradient(circle at 32% 28%, rgba(255,255,255,0.22), transparent 60%), ${color}`,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: Math.round(size * 0.45),
        fontWeight: 800,
        letterSpacing: 0,
        flexShrink: 0,
        boxShadow: `0 1px 3px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(255,255,255,0.06)`,
        textShadow: "0 1px 1px rgba(0,0,0,0.18)",
      }}
    >
      {symbol.charAt(0)}
    </div>
  );
}

// Output amount cell — supports loading shimmer + a subtle pulse when
// a silent refresh swaps the value in place.
function ReceiveAmount({
  quote,
  loading,
  pulse,
  toAssetId,
}: {
  quote: SwapQuote | null;
  loading: boolean;
  pulse: boolean;
  /** Destination asset id, e.g. "ETH.ETH" — drives the decimal fallback
   *  when the quote's raw is missing `out_asset_decimal`. */
  toAssetId: string;
}) {
  if (loading) {
    return (
      <span
        className="skel"
        aria-hidden
        style={{
          display: "inline-block",
          flex: 1,
          height: 40,
          borderRadius: 10,
        }}
      />
    );
  }
  const text = quote ? fmtFromQuote(quote.expected_amount_out, quote, toAssetId) : "0";
  return (
    <span
      className={quote && pulse ? "flash-on-refresh" : ""}
      style={{
        flex: 1,
        minWidth: 0,
        fontSize: 38,
        fontWeight: 600,
        letterSpacing: "-0.025em",
        lineHeight: 1.1,
        color: quote ? "var(--text)" : "var(--hint-soft)",
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {text}
    </span>
  );
}

// ─── Live quote summary ────────────────────────────────────────────────

function QuoteSummary({
  quote,
  target,
  amountIn,
  fromTicker,
  showZecNetworkFee,
  feeZats,
  fetchedAt,
  refreshing,
  slippagePct,
  protocol,
  onOpenSettings,
  onRefresh,
}: {
  quote: SwapQuote;
  target: Target;
  amountIn: number;
  fromTicker: string;
  /**
   * Only ZEC origin has a known-in-advance local network fee (the
   * ZIP-317 shielded send fee). EVM gas estimates land at sign time —
   * we hide the row rather than show a misleading 0.
   */
  showZecNetworkFee: boolean;
  feeZats: number;
  fetchedAt: number;
  refreshing: boolean;
  slippagePct: number;
  /** "mayachain", "thorchain", "chainflip", … — shown on the Route row. */
  protocol: string;
  onOpenSettings: () => void;
  onRefresh: () => void;
}) {
  // All atomic → human conversions go through the BigInt-safe helper.
  // `out` and `protoFeeOut` are Numbers (we need them for slippage
  // math + display formatting downstream); higher-precision strings
  // would be overkill for human-scale amounts and the slippage math
  // is inherently lossy anyway.
  const out = numFromQuote(quote.expected_amount_out, quote, target.id);
  const rate = amountIn > 0 ? out / amountIn : 0;
  const minReceived = out * (1 - slippagePct / 100);
  const protoFeeOut = quote.fees.total
    ? numFromQuote(quote.fees.total, quote, target.id)
    : null;
  const ageS = Math.max(0, Math.floor((Date.now() - fetchedAt) / 1000));
  const eta = Math.max(60, quote.total_swap_seconds);

  return (
    <div className="quote-card quote-pop">
      {/* ── Header row: rate + live-pulse + refresh ───────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            minWidth: 0,
          }}
        >
          <span className="live-dot" aria-hidden />
          <span
            style={{
              fontSize: 13,
              color: "var(--text-soft)",
              letterSpacing: "-0.005em",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            1 {fromTicker} ={" "}
            <span
              style={{
                color: "var(--text)",
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {rate.toFixed(6).replace(/\.?0+$/, "")}
            </span>{" "}
            {target.symbol}
          </span>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          aria-label="Refresh quote"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            background: "transparent",
            border: "none",
            color: "var(--hint)",
            cursor: "pointer",
            fontFamily: "var(--font-text)",
            fontSize: 11,
            fontWeight: 600,
            padding: 0,
            flexShrink: 0,
          }}
        >
          <span className={refreshing ? "spin" : ""} style={{ display: "inline-flex" }}>
            <Ico.refresh width={12} height={12} />
          </span>
          {ageS < 2 ? "just now" : `${ageS}s ago`}
        </button>
      </div>

      <Hairline />

      <Row
        label="ETA"
        right={<EtaCell seconds={eta} />}
      />
      <Row
        label="Route"
        right={<RouteBadge protocol={protocol} />}
      />
      {showZecNetworkFee ? (
        <Row
          label="Network fee"
          right={
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              {(feeZats / 1e8).toFixed(6).replace(/\.?0+$/, "")} ZEC
            </span>
          }
        />
      ) : (
        <Row
          label="Gas"
          right={<span style={{ color: "var(--hint)" }}>est. at sign time</span>}
        />
      )}
      {protoFeeOut != null && (
        <Row
          label="Protocol fee"
          right={
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              {protoFeeOut.toFixed(6).replace(/\.?0+$/, "")} {target.symbol}
            </span>
          }
        />
      )}
      <Row
        label={
          <button
            type="button"
            onClick={onOpenSettings}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "transparent",
              border: "none",
              color: "var(--hint)",
              cursor: "pointer",
              padding: 0,
              fontFamily: "var(--font-text)",
              fontSize: 13,
            }}
          >
            <Ico.settings width={14} height={14} />
            Slippage
          </button>
        }
        right={
          <button
            type="button"
            onClick={onOpenSettings}
            style={{
              background: "var(--bg-3)",
              border: "1px solid var(--hairline)",
              color: "var(--text)",
              cursor: "pointer",
              fontFamily: "var(--font-text)",
              fontSize: 12,
              fontWeight: 600,
              padding: "3px 10px",
              borderRadius: 999,
            }}
          >
            {fmtSlippage(slippagePct)}
          </button>
        }
      />
      <Row
        label="Min received"
        right={
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {minReceived.toFixed(6).replace(/\.?0+$/, "")} {target.symbol}
          </span>
        }
      />
      {quote.notes && (
        <div
          style={{
            marginTop: 10,
            padding: 10,
            background: "rgba(255, 200, 80, 0.08)",
            border: "1px solid rgba(255, 200, 80, 0.16)",
            borderRadius: 10,
            fontSize: 11,
            color: "var(--text-soft)",
            lineHeight: 1.45,
          }}
        >
          {quote.notes}
        </div>
      )}
    </div>
  );
}

// Route badge — small coloured dot + protocol name, matching the
// ProtocolBadge atom but stylable inline for the quote summary.
function RouteBadge({ protocol }: { protocol: string }) {
  const colors: Record<string, string> = {
    mayachain: "#4B6BC9",
    thorchain: "#23DCC8",
    chainflip: "#FFC400",
    relay: "#8B5CF6",
    rango: "#94A3B8",
    across: "#FFD400",
    debridge: "#0EA5E9",
  };
  const c = colors[protocol.toLowerCase()] ?? "#94A3B8";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 13,
        fontWeight: 600,
        color: "var(--text)",
        textTransform: "capitalize",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          background: c,
          boxShadow: `0 0 0 3px ${c}22`,
        }}
      />
      {protocol}
    </span>
  );
}

function EtaCell({ seconds }: { seconds: number }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <ClockIcon />
      <span style={{ fontVariantNumeric: "tabular-nums" }}>~{seconds}s</span>
    </span>
  );
}

function ClockIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function Row({
  label,
  right,
}: {
  label: React.ReactNode;
  right: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        padding: "5px 0",
        fontSize: 13,
        color: "var(--hint)",
      }}
    >
      <span style={{ minWidth: 0 }}>{label}</span>
      <span
        style={{
          color: "var(--text)",
          fontWeight: 500,
          whiteSpace: "nowrap",
        }}
      >
        {right}
      </span>
    </div>
  );
}

function Hairline() {
  return (
    <div
      style={{
        height: 1,
        background: "var(--hairline, #2c333a)",
        margin: "6px 0",
        opacity: 0.6,
      }}
    />
  );
}

function fmtSlippage(p: number): string {
  if (Number.isInteger(p)) return `${p}%`;
  return `${p.toFixed(2).replace(/\.?0+$/, "")}%`;
}

// ─── Slippage settings inline panel ────────────────────────────────────

function SlippagePanel({
  current,
  custom,
  onPreset,
  onCustom,
  onClose,
}: {
  current: number;
  custom: string;
  onPreset: (p: number) => void;
  onCustom: (v: string) => void;
  onClose: () => void;
}) {
  return (
    <Card
      style={{
        padding: 14,
        marginTop: 8,
        background: "var(--bg-2)",
        border: "1px solid var(--hairline, #2c333a)",
        borderRadius: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600 }}>
          Slippage tolerance
        </span>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "transparent",
            border: "none",
            color: "var(--hint)",
            cursor: "pointer",
            fontFamily: "var(--font-text)",
            fontSize: 12,
          }}
        >
          Done
        </button>
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        {SLIPPAGE_PRESETS.map((p) => {
          const active = custom.trim() === "" && current === p;
          return (
            <button
              key={p}
              type="button"
              onClick={() => onPreset(p)}
              style={{
                flex: 1,
                padding: "8px 10px",
                background: active ? "var(--text)" : "var(--bg-3, #1F242D)",
                color: active ? "var(--bg)" : "var(--text)",
                border: "none",
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "var(--font-text)",
              }}
            >
              {p}%
            </button>
          );
        })}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flex: 1.2,
            padding: "6px 10px",
            background: "var(--bg-3, #1F242D)",
            borderRadius: 10,
          }}
        >
          <input
            value={custom}
            onChange={(e) => onCustom(e.target.value)}
            placeholder="Custom"
            inputMode="decimal"
            style={{
              width: "100%",
              background: "transparent",
              border: "none",
              outline: "none",
              fontSize: 13,
              fontFamily: "var(--font-text)",
              color: "var(--text)",
              textAlign: "right",
              padding: 0,
            }}
          />
          <span style={{ marginLeft: 4, color: "var(--hint)", fontSize: 13 }}>%</span>
        </div>
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: "var(--hint)", lineHeight: 1.4 }}>
        Passed to the aggregator as <code>slippage_bps</code> on every quote
        and also enforced locally for the min-received display.
      </div>
    </Card>
  );
}

// ─── Token picker bottom sheet ─────────────────────────────────────────

function TokenPickerSheet({
  open,
  onClose,
  search,
  onSearch,
  selectedId,
  excludeId,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  search: string;
  onSearch: (v: string) => void;
  selectedId: string;
  /** When provided, hides this asset (used to prevent same-asset selection). */
  excludeId?: string;
  onSelect: (id: string) => void;
}) {
  const q = search.trim().toLowerCase();
  const filtered = TARGETS.filter(
    (t) =>
      t.id !== excludeId &&
      (!q ||
        t.symbol.toLowerCase().includes(q) ||
        t.chain.toLowerCase().includes(q) ||
        t.id.toLowerCase().includes(q)),
  );

  // Group by chain so the list mirrors what users mentally model
  // ("USDC on Arbitrum" vs "USDC on Ethereum" — the chain is the
  // distinguishing axis).
  const grouped = new Map<string, Target[]>();
  for (const t of filtered) {
    const arr = grouped.get(t.chain) ?? [];
    arr.push(t);
    grouped.set(t.chain, arr);
  }

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <Drawer.Portal>
        <Drawer.Overlay
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 50,
          }}
        />
        <Drawer.Content
          aria-describedby={undefined}
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 51,
            display: "flex",
            flexDirection: "column",
            maxHeight: "82dvh",
            background: "var(--bg, #0E1116)",
            color: "var(--text)",
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            boxShadow: "0 -12px 28px rgba(0,0,0,0.4)",
            outline: "none",
            fontFamily: "var(--font-text)",
          }}
        >
          <div
            style={{
              padding: "10px 0 6px",
              flexShrink: 0,
              cursor: "grab",
              touchAction: "none",
            }}
          >
            <div
              style={{
                width: 40,
                height: 4,
                borderRadius: 999,
                background: "var(--hint-soft, rgba(255,255,255,0.32))",
                margin: "0 auto",
              }}
            />
          </div>
          <Drawer.Title
            style={{
              padding: "4px 18px 10px",
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: "-0.01em",
            }}
          >
            Select asset
          </Drawer.Title>
          {/* Search input */}
          <div
            style={{
              padding: "0 18px 12px",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 12px",
                background: "var(--bg-2)",
                border: "1px solid var(--hairline, #2c333a)",
                borderRadius: 12,
              }}
            >
              <Ico.search />
              <input
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder="Search by name or chain"
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  fontSize: 14,
                  color: "var(--text)",
                  fontFamily: "var(--font-text)",
                }}
              />
            </div>
          </div>
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              padding: "0 8px max(22px, env(safe-area-inset-bottom))",
            }}
          >
            {filtered.length === 0 && (
              <div
                style={{
                  padding: 24,
                  textAlign: "center",
                  color: "var(--hint)",
                  fontSize: 13,
                }}
              >
                Nothing matches "{search}".
              </div>
            )}
            {[...grouped.entries()].map(([chain, items]) => (
              <div key={chain} style={{ marginBottom: 12 }}>
                <div
                  style={{
                    padding: "8px 12px 6px",
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    color: "var(--hint)",
                  }}
                >
                  {chain}
                </div>
                {items.map((t) => {
                  const active = t.id === selectedId;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => onSelect(t.id)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "12px",
                        background: active
                          ? "rgba(244, 183, 40, 0.08)"
                          : "transparent",
                        border: "none",
                        borderRadius: 12,
                        textAlign: "left",
                        cursor: "pointer",
                        fontFamily: "var(--font-text)",
                        color: "var(--text)",
                      }}
                    >
                      <TokenGlyph symbol={t.symbol} color={t.color} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-0.005em" }}>
                          {t.symbol}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--hint)" }}>
                          {t.chain}
                        </div>
                      </div>
                      {active && (
                        <span style={{ color: "var(--accent, #F4B728)" }}>
                          <Ico.check />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

// ─── From-asset picker (ZEC + every held EVM asset) ────────────────────

function FromAssetPickerSheet({
  open,
  onClose,
  search,
  onSearch,
  zecBalanceZats,
  evmHoldings,
  selectedId,
  excludeId,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  search: string;
  onSearch: (v: string) => void;
  /** Shielded ZEC balance in zats — surfaces "ZEC.ZEC" always. */
  zecBalanceZats: number;
  /** Flat EVM holdings from useEvmHoldings, or null while it loads. */
  evmHoldings: import("../evm/balances").EvmHoldings | null;
  selectedId: string;
  /**
   * Asset id to omit from the list (the currently-selected to-asset).
   * Prevents the user from picking USDC→USDC, which is a degenerate
   * swap the aggregator will quote but can't sensibly route.
   */
  excludeId: string;
  onSelect: (id: string) => void;
}) {
  const q = search.trim().toLowerCase();
  // Build a flat list: ZEC first, then every EVM holding with balance > 0.
  // Each row carries the LeoKit asset id (token_symbol), a display ticker,
  // chain label, chain colour for the glyph, balance + USD value.
  interface Row {
    id: string;
    ticker: string;
    chain: string;
    color: string;
    balance: number;
    balanceUsd: number;
  }
  const rows: Row[] = [];
  rows.push({
    id: "ZEC.ZEC",
    ticker: "ZEC",
    chain: "Zcash · shielded",
    color: "#F4B728",
    balance: zecBalanceZats / 1e8,
    balanceUsd: 0, // we don't have a ZEC price feed in this hook
  });
  if (evmHoldings) {
    for (const c of evmHoldings.perChain) {
      const chainCfg = EVM_CHAINS.find((x) => x.id === c.chain);
      for (const t of c.tokens) {
        if (t.balance <= 0) continue;
        rows.push({
          id: t.token_symbol,
          ticker: t.symbol,
          chain: chainCfg?.name ?? c.chain,
          color: chainCfg?.color ?? "#888",
          balance: t.balance,
          balanceUsd: t.balance_usd,
        });
      }
    }
  }
  const filtered = rows.filter((r) => {
    if (r.id === excludeId) return false;
    if (!q) return true;
    return (
      r.ticker.toLowerCase().includes(q) ||
      r.chain.toLowerCase().includes(q) ||
      r.id.toLowerCase().includes(q)
    );
  });

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <Drawer.Portal>
        <Drawer.Overlay
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 50,
          }}
        />
        <Drawer.Content
          aria-describedby={undefined}
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 51,
            display: "flex",
            flexDirection: "column",
            maxHeight: "82dvh",
            background: "var(--bg, #0E1116)",
            color: "var(--text)",
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            boxShadow: "0 -12px 28px rgba(0,0,0,0.4)",
            outline: "none",
            fontFamily: "var(--font-text)",
          }}
        >
          <div
            style={{
              padding: "10px 0 6px",
              flexShrink: 0,
              cursor: "grab",
              touchAction: "none",
            }}
          >
            <div
              style={{
                width: 40,
                height: 4,
                borderRadius: 999,
                background: "var(--hint-soft, rgba(255,255,255,0.32))",
                margin: "0 auto",
              }}
            />
          </div>
          <Drawer.Title
            style={{
              padding: "4px 18px 10px",
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: "-0.01em",
            }}
          >
            Pay with
          </Drawer.Title>
          <div style={{ padding: "0 18px 12px", flexShrink: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 12px",
                background: "var(--bg-2)",
                border: "1px solid var(--hairline, #2c333a)",
                borderRadius: 12,
              }}
            >
              <Ico.search />
              <input
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder="Search by asset or chain"
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  fontSize: 14,
                  color: "var(--text)",
                  fontFamily: "var(--font-text)",
                }}
              />
            </div>
          </div>
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              padding: "0 8px max(22px, env(safe-area-inset-bottom))",
            }}
          >
            {filtered.length === 0 && (
              <div
                style={{
                  padding: 24,
                  textAlign: "center",
                  color: "var(--hint)",
                  fontSize: 13,
                }}
              >
                {q
                  ? `Nothing matches "${search}".`
                  : "No spendable balances yet. Receive funds to your wallet first."}
              </div>
            )}
            {filtered.map((r) => {
              const active = r.id === selectedId;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => onSelect(r.id)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px",
                    background: active
                      ? "rgba(244, 183, 40, 0.08)"
                      : "transparent",
                    border: "none",
                    borderRadius: 12,
                    textAlign: "left",
                    cursor: "pointer",
                    fontFamily: "var(--font-text)",
                    color: "var(--text)",
                  }}
                >
                  <TokenGlyph symbol={r.ticker} color={r.color} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 600,
                        letterSpacing: "-0.005em",
                      }}
                    >
                      {r.ticker}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--hint)" }}>
                      {r.chain}
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {r.balance.toLocaleString(undefined, {
                        maximumFractionDigits: 6,
                      })}
                    </div>
                    {r.balanceUsd > 0 && (
                      <div style={{ fontSize: 11, color: "var(--hint)" }}>
                        ${r.balanceUsd.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    )}
                  </div>
                  {active && (
                    <span style={{ color: "var(--accent, #F4B728)" }}>
                      <Ico.check />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

// ─── Confirmation bottom sheet ─────────────────────────────────────────

function ConfirmSheet({
  open,
  onClose,
  stage,
  target,
  amountIn,
  fromTicker,
  fromIsZec,
  fromAddress,
  slippagePct,
  toAddress,
  onConfirm,
  disabled,
}: {
  open: boolean;
  onClose: () => void;
  stage:
    | {
        kind: "quoted";
        quote: SwapQuote;
        protocol: string;
        depositAddress: string;
        memoUa: string;
        memoText: string;
        feeZats: number;
        fetchedAt: number;
        refreshing: boolean;
      }
    | null;
  target: Target;
  /** Raw amount input string (already in source-asset units). */
  amountIn: string;
  /** Display ticker, e.g. "ZEC" / "ETH" / "USDC". */
  fromTicker: string;
  /** Branches the network-fee row + "from" subtitle / address row. */
  fromIsZec: boolean;
  /** Derived EVM address — surfaced as a "From" row on EVM swaps. */
  fromAddress: `0x${string}` | null;
  slippagePct: number;
  toAddress: string;
  onConfirm: () => void;
  disabled: boolean;
}) {
  if (!stage) {
    return null;
  }
  const out = numFromQuote(stage.quote.expected_amount_out, stage.quote, target.id);
  const minReceived = out * (1 - slippagePct / 100);
  const eta = Math.max(60, stage.quote.total_swap_seconds);
  const protoFeeOut = stage.quote.fees.total
    ? numFromQuote(stage.quote.fees.total, stage.quote, target.id)
    : null;
  return (
    <Drawer.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <Drawer.Portal>
        <Drawer.Overlay
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 50,
          }}
        />
        <Drawer.Content
          aria-describedby={undefined}
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 51,
            display: "flex",
            flexDirection: "column",
            background: "var(--bg, #0E1116)",
            color: "var(--text)",
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            boxShadow: "0 -12px 28px rgba(0,0,0,0.4)",
            outline: "none",
            fontFamily: "var(--font-text)",
            maxHeight: "92dvh",
          }}
        >
          <div
            style={{
              padding: "10px 0 6px",
              flexShrink: 0,
              cursor: "grab",
              touchAction: "none",
            }}
          >
            <div
              style={{
                width: 40,
                height: 4,
                borderRadius: 999,
                background: "var(--hint-soft, rgba(255,255,255,0.32))",
                margin: "0 auto",
              }}
            />
          </div>
          <Drawer.Title
            style={{
              padding: "4px 18px 6px",
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: "-0.01em",
            }}
          >
            Review swap
          </Drawer.Title>
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              padding: "8px 18px max(22px, env(safe-area-inset-bottom))",
            }}
          >
            {/* Big hero — pay / receive, stacked Uniswap-style */}
            <div
              style={{
                background: "var(--bg-2)",
                border: "1px solid var(--hairline)",
                borderRadius: 18,
                padding: 0,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <ConfirmRow
                label="You pay"
                amount={amountIn}
                ticker={fromTicker}
                subtitle={fromIsZec ? "From shielded ZEC" : "From EVM wallet"}
                glyphColor={fromIsZec ? "#F4B728" : "#627EEA"}
              />
              <div
                style={{
                  height: 1,
                  background: "var(--hairline)",
                  margin: "0 14px",
                }}
              />
              <ConfirmRow
                label="You receive"
                amount={out.toFixed(6).replace(/\.?0+$/, "")}
                ticker={target.symbol}
                subtitle={`On ${target.chain}`}
                glyphColor={target.color}
                accent
              />
              {/* Centered chevron joining the two rows */}
              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%) rotate(90deg)",
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  background: "var(--bg-2)",
                  border: "1px solid var(--hairline)",
                  boxShadow: "0 0 0 3px var(--bg)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--hint)",
                }}
              >
                <Ico.chevRight />
              </div>
            </div>

            <div
              style={{
                marginTop: 14,
                background: "var(--bg-2)",
                border: "1px solid var(--hairline, #2c333a)",
                borderRadius: 16,
                padding: "10px 14px",
              }}
            >
              {!fromIsZec && fromAddress && (
                <>
                  <Row label="From" right={
                    <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                      {truncateAddr(fromAddress, 6, 4)}
                    </span>
                  } />
                  <Hairline />
                </>
              )}
              <Row label="Recipient" right={
                <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                  {truncateAddr(toAddress, 8, 6)}
                </span>
              } />
              <Hairline />
              <Row label="Min received" right={
                <span style={{ fontVariantNumeric: "tabular-nums" }}>
                  {minReceived.toFixed(6).replace(/\.?0+$/, "")} {target.symbol}
                </span>
              } />
              <Row label={`Slippage`} right={fmtSlippage(slippagePct)} />
              <Hairline />
              {fromIsZec ? (
                <Row label="Network fee" right={
                  <span style={{ fontVariantNumeric: "tabular-nums" }}>
                    {(stage.feeZats / 1e8).toFixed(6).replace(/\.?0+$/, "")} ZEC
                  </span>
                } />
              ) : (
                <Row label="Gas" right={
                  <span style={{ color: "var(--hint)" }}>est. at sign time</span>
                } />
              )}
              {protoFeeOut != null && (
                <Row label="Protocol fee" right={
                  <span style={{ fontVariantNumeric: "tabular-nums" }}>
                    {protoFeeOut.toFixed(6).replace(/\.?0+$/, "")} {target.symbol}
                  </span>
                } />
              )}
              <Row label="ETA" right={<EtaCell seconds={eta} />} />
              <Row label="Route" right={
                <span style={{ textTransform: "capitalize" }}>
                  {stage.protocol}
                </span>
              } />
            </div>

            <div
              style={{
                marginTop: 12,
                fontSize: 11,
                color: "var(--hint)",
                lineHeight: 1.45,
              }}
            >
              {fromIsZec
                ? "Proofs will run on your device (~30–90s on most phones). Don't close this tab — Telegram will warn you if you swipe down."
                : "We'll sign the transaction with your derived EVM key and broadcast it from your device. No approval step — if the router needs ERC-20 allowance, the tx will revert and we'll surface it."}
            </div>

            <div style={{ marginTop: 16 }}>
              <MainButton
                text="Confirm swap"
                onClick={onConfirm}
                disabled={disabled}
              />
              <button
                type="button"
                onClick={onClose}
                style={{
                  marginTop: 10,
                  width: "100%",
                  padding: "12px",
                  background: "transparent",
                  color: "var(--hint)",
                  border: "1px solid var(--hairline, #2c333a)",
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "var(--font-text)",
                }}
              >
                Edit
              </button>
            </div>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

// Confirm-sheet row — pay / receive vertically stacked, with token glyph
// on the left and the amount + chain caption.
function ConfirmRow({
  label,
  amount,
  ticker,
  subtitle,
  glyphColor,
  accent,
}: {
  label: string;
  amount: string;
  ticker: string;
  subtitle: string;
  glyphColor: string;
  /** Slightly raised background tint for the receive side. */
  accent?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "16px 16px",
        background: accent ? "rgba(240, 180, 0, 0.04)" : "transparent",
      }}
    >
      <TokenGlyph symbol={ticker} color={glyphColor} size={36} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 11,
            color: "var(--hint)",
            textTransform: "uppercase",
            letterSpacing: 1,
            marginBottom: 4,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: "-0.015em",
            color: "var(--text)",
            lineHeight: 1.1,
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {amount} <span style={{ color: "var(--text-soft)", fontWeight: 600 }}>{ticker}</span>
        </div>
        <div
          style={{
            fontSize: 11,
            color: "var(--hint)",
            marginTop: 2,
          }}
        >
          {subtitle}
        </div>
      </div>
    </div>
  );
}

// ─── CTA state machine ────────────────────────────────────────────────

interface CtaState {
  text: string;
  disabled: boolean;
  destructive?: boolean;
  action: "none" | "confirm";
}

function computeCta(args: {
  stageKind: Stage["kind"];
  amount: string;
  amountValid: boolean;
  overBalance: boolean;
  toAddressTrim: string;
  /**
   * Whether the signer for the source asset is ready — shielded keys
   * for ZEC, derived EVM address for EVM. Same disabled semantics as
   * the old `hasKeys` flag, just polymorphic.
   */
  hasSigner: boolean;
  /**
   * from === to is a degenerate case — LeoKit will quote it but the
   * deposit makes no sense. We disable the CTA with a clear message.
   */
  sameAsset: boolean;
  fromTicker: string;
}): CtaState {
  if (args.stageKind === "sending") {
    return { text: "Working…", disabled: true, action: "none" };
  }
  if (args.stageKind === "quoting") {
    return { text: "Fetching quote…", disabled: true, action: "none" };
  }
  if (args.sameAsset) {
    return {
      text: "Pick a different destination",
      disabled: true,
      action: "none",
    };
  }
  if (!args.amount.trim() || !args.amountValid) {
    return { text: "Enter an amount", disabled: true, action: "none" };
  }
  if (args.overBalance) {
    return {
      text: `Insufficient ${args.fromTicker} balance`,
      disabled: true,
      destructive: true,
      action: "none",
    };
  }
  if (args.toAddressTrim.length < 8) {
    return { text: "Enter destination address", disabled: true, action: "none" };
  }
  if (args.stageKind === "quoted") {
    return {
      text: "Confirm swap",
      disabled: !args.hasSigner,
      action: "confirm",
    };
  }
  // Form is valid but no quote yet — the auto-quote effect is about to
  // fire (or has fired and is in flight). Show a friendly "preparing"
  // CTA so the user understands the button will activate soon.
  return {
    text: "Preparing quote…",
    disabled: true,
    action: "none",
  };
}

// ─── Misc helpers (preserved from previous version) ───────────────────

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 1,
          color: "var(--hint)",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 500, marginTop: 2 }}>
        {children}
      </div>
    </div>
  );
}

function fmtElapsed(s: number): string {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}m ${rem}s`;
}

function ProgressBar({ pct }: { pct: number }) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div
      style={{
        marginTop: 10,
        height: 6,
        background: "var(--bg-3, #1F242D)",
        borderRadius: 999,
        overflow: "hidden",
        position: "relative",
      }}
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        style={{
          width: `${clamped}%`,
          height: "100%",
          background:
            "linear-gradient(90deg, var(--success, #34C759), var(--accent, #F0B400))",
          borderRadius: 999,
          transition: "width 200ms ease-out",
          boxShadow: "0 0 8px rgba(52,199,89,0.35)",
        }}
      />
      <div
        style={{
          position: "absolute",
          right: 4,
          top: -16,
          fontSize: 10,
          color: "var(--hint)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {clamped}%
      </div>
    </div>
  );
}

function progressLabel(stage: SendApi.SendProgress["stage"]): string {
  switch (stage) {
    case "syncing-witnesses":
      return "Syncing witnesses…";
    case "loading-params":
      return "Loading proving keys…";
    case "selecting-coins":
      return "Selecting notes…";
    case "proving":
      return "Generating zero-knowledge proofs…";
    case "broadcasting":
      return "Broadcasting…";
    case "done":
      return "Done";
  }
}

function progressHint(stage: SendApi.SendProgress["stage"]): string {
  switch (stage) {
    case "proving":
      return "Don't close this tab.";
    case "loading-params":
      return "First swap only — Sapling params decompress (5-30 s).";
    default:
      return "";
  }
}

// Quiet the unused-import lint complaint — Spinner is referenced by the
// MainButton state via the loading prop, but TS still tracks the import.
// Keep this no-op around so subsequent edits don't accidentally drop it.
void Spinner;
